import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';

export interface AISelectionData {
  name: string;
  description: string;
  images: string[];
  features?: string[];
  price?: number;
  currency?: string;
  type?: 'product' | 'service';
  timestamp?: number;
}

export interface AISelectionState {
  data: AISelectionData | null;
  shouldOpenForm: boolean;
  targetTab?: 'product' | 'service';
}

@Injectable({ providedIn: 'root' })
export class AiChatSelectionService {
  // Option 3: Enhanced service with persistence
  private selectionSubject = new Subject<AISelectionData>();
  private selectionState = new BehaviorSubject<AISelectionState>({
    data: null,
    shouldOpenForm: false,
    targetTab: undefined
  });

  selection$ = this.selectionSubject.asObservable();
  selectionState$ = this.selectionState.asObservable();

  // Persistent storage key
  private readonly STORAGE_KEY = 'aiSelectionData';

  constructor() {
    // Option 3: Load any persisted data on service init
    this.loadPersistedData();
  }

  emitSelection(data: AISelectionData) {
    console.log('🔄 Option 3: Emitting AI selection via enhanced service:', data);
    
    // Add timestamp
    const enhancedData = { ...data, timestamp: Date.now() };
    
    // Persist to localStorage for cross-navigation reliability
    this.persistData(enhancedData);
    
    // Update state
    this.selectionState.next({
      data: enhancedData,
      shouldOpenForm: true,
      targetTab: enhancedData.type
    });
    
    // Also emit via original subject for backward compatibility
    this.selectionSubject.next(enhancedData);
  }

  // Get current selection state
  getCurrentSelection(): AISelectionState {
    return this.selectionState.value;
  }

  // Check and consume pending selection
  consumePendingSelection(): AISelectionData | null {
    const currentState = this.selectionState.value;
    
    if (currentState.shouldOpenForm && currentState.data) {
      console.log('🔄 Option 3: Consuming pending AI selection:', currentState.data);
      
      // Clear the state to prevent re-consumption
      this.clearSelection();
      
      return currentState.data;
    }
    
    return null;
  }

  // Clear selection state
  clearSelection() {
    console.log('🔄 Option 3: Clearing AI selection state');
    this.selectionState.next({
      data: null,
      shouldOpenForm: false,
      targetTab: undefined
    });
    this.clearPersistedData();
  }

  // Persist data to localStorage
  private persistData(data: AISelectionData) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      console.log('🔄 Option 3: Data persisted to localStorage');
    } catch (error) {
      console.error('❌ Option 3: Failed to persist data:', error);
    }
  }

  // Load persisted data
  private loadPersistedData() {
    try {
      const persistedData = localStorage.getItem(this.STORAGE_KEY);
      
      if (persistedData) {
        const data: AISelectionData = JSON.parse(persistedData);
        
        // Check if data is fresh (within 5 minutes)
        const isRecent = data.timestamp && (Date.now() - data.timestamp < 300000);
        
        if (isRecent) {
          console.log('🔄 Option 3: Loaded fresh persisted data:', data);
          this.selectionState.next({
            data,
            shouldOpenForm: true,
            targetTab: data.type
          });
        } else {
          console.log('🔄 Option 3: Persisted data is stale, clearing');
          this.clearPersistedData();
        }
      }
    } catch (error) {
      console.error('❌ Option 3: Failed to load persisted data:', error);
      this.clearPersistedData();
    }
  }

  // Clear persisted data
  private clearPersistedData() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('❌ Option 3: Failed to clear persisted data:', error);
    }
  }
}
